import React, { useMemo, useCallback } from 'react';
import FieldVisibilityDropdown from '../FieldVisibilityDropdown';
import VisibilityDropdown from '../VisibilityDropdown';
import KanbanFilterDropdown from '../KanbanFilterDropdown/index';
import { StatusLimitsDropdown } from '../ColumnLimitsDropdown';
import { calculateLockStates } from '../../FlowchartView/utils/dependencyUtils';
import { isTaskActionable } from '../../shared/utils/taskAvailabilityUtils';

import { ToolbarBtn } from './ToolbarButton';
import FokusButton from '../../shared/FokusButton';
import {
  MissionIcon,
  DiamondIcon,
  HourglassIcon,
  SearchIcon,
  ResponsiveToolbar,
} from '../../shared/ToolbarPrimitives';
import ResetCampaignToolbarItem from '../ResetCampaignToolbarItem';
import VariablesToolbarItem from '../VariablesToolbarItem';
import {
  HomeIcon,
} from './icons';

// ═══════════════════════════════════════════════════════════════
// MAIN TOOLBAR
// ═══════════════════════════════════════════════════════════════

const FlowToolbar = ({
  flowchartRef,
  isFocusMode,
  hasSelection,
  tasks,
  categories,
  statuses,
  allTags,
  onAddTask,
  onAddDecision,
  onAddWait,
  onFocusSelected,
  onExitFocusMode,
  onFilterFocus,
  // Card display settings
  cardDisplaySettings,
  onCardDisplaySettingsChange,
  // Custom fields (campaign-level definitions)
  customFields = [],
  onManageCustomFields,
  // Nested sprints for actionable calculation
  nestedSprints = [],
  // Multi-campaign mode
  isMultiCampaignMode = false,
  onOpenCampaignPicker,
  // HUD visibility settings
  flowViewVisibility = {},
  onFlowViewVisibilityChange,
  // Status limits (WIP limits)
  onSetWipLimit,
  // Normal filter (same as other views)
  filters = {},
  onFiltersChange,
  showNestedCampaigns = true,
  onShowNestedCampaignsChange,
  showActionableOnly = false,
  onToggleActionableOnly,
  // Mission search
  onOpenMissionSearch,
  onResetCampaignProgress,
  onManageVariables = null,
  // Board has the same FOKUS mode as the other views.
  hideCanvasControls = false,
  onGoToStart = null,
  selectedCount,
}) => {
  // Calculate lock states and actionable task IDs
  const lockStates = useMemo(() => {
    return calculateLockStates(tasks, nestedSprints, statuses);
  }, [tasks, nestedSprints, statuses]);

  const actionableTaskIds = useMemo(() => {
    return tasks
      .filter(task => isTaskActionable(task, lockStates, nestedSprints, statuses))
      .map(task => task.id);
  }, [tasks, lockStates, nestedSprints, statuses]);

  // In multi-campaign mode, "Add Mission" should open the campaign picker
  const handleAddMission = useCallback(() => {
    if (isMultiCampaignMode && onOpenCampaignPicker) {
      onOpenCampaignPicker();
    } else {
      onAddTask?.();
    }
  }, [isMultiCampaignMode, onOpenCampaignPicker, onAddTask]);

  return (
    <ResponsiveToolbar>
      {/* ═══════════════════════════════════════════════════════════════
          SHARED ELEMENTS (same order across ALL views)
          Priority 1-3 = most important, hidden last
          ═══════════════════════════════════════════════════════════════ */}
      
      {/* 1-2. Add Mission/Decision buttons (ALL views) - Priority 1 */}
      <ResponsiveToolbar.Item priority={1}>
        <div className="unified-toolbar__group">
          <ToolbarBtn
            icon={<MissionIcon />}
            label="Add Mission"
            onClick={handleAddMission}
            title="Add new mission"
            variant="accent"
          />
          <ToolbarBtn
            icon={<DiamondIcon />}
            label="Add Decision"
            onClick={onAddDecision}
            title="Add decision node"
          />
          <ToolbarBtn
            icon={<HourglassIcon />}
            label="Add Wait for"
            onClick={onAddWait}
            title="Add a wait-for card"
          />
        </div>
      </ResponsiveToolbar.Item>

      {/* 3. FOKUS Button (ALL views) - Priority 2 */}
      {!hideCanvasControls && (
      <ResponsiveToolbar.Item priority={2}>
        <FokusButton
          isFocusMode={isFocusMode}
          hasSelection={hasSelection}
          selectedCount={selectedCount ?? (hasSelection ? 1 : 0)}
          actionableTaskIds={actionableTaskIds}
          onFocusSelected={onFocusSelected}
          onExitFocusMode={onExitFocusMode}
          onFilterFocus={onFilterFocus}
          tasks={tasks}
          categories={categories}
          statuses={statuses}
          allTags={allTags}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          selectionLabel="Selected Nodes"
        />
      </ResponsiveToolbar.Item>
      )}

      {/* 4. Filter (ALL views) - Priority 3 */}
      <ResponsiveToolbar.Item priority={3}>
        <KanbanFilterDropdown
          filters={filters}
          onFiltersChange={onFiltersChange}
          statuses={statuses}
          categories={categories}
          allTags={allTags}
          tasks={tasks}
          showNestedCampaigns={showNestedCampaigns}
          onShowNestedCampaignsChange={onShowNestedCampaignsChange}
          lockStates={lockStates}
          nestedSprints={nestedSprints}
          showActionableOnly={showActionableOnly}
          onToggleActionableOnly={onToggleActionableOnly}
          customFields={customFields}
        />
      </ResponsiveToolbar.Item>

      {/* 5. WIP Limits (ALL views) - Priority 5 */}
      <ResponsiveToolbar.Item priority={5}>
        <StatusLimitsDropdown
          statuses={statuses}
          tasks={tasks}
          onSetWipLimit={onSetWipLimit}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BY 4 VIEWS (Flow, Kanban, Table, Todo - not Gantt)
          ═══════════════════════════════════════════════════════════════ */}

      {/* 9. Field Visibility (Flow, Kanban, Table, Todo) - Priority 9 */}
      <ResponsiveToolbar.Item priority={9}>
        <FieldVisibilityDropdown
          view="flow"
          visibility={cardDisplaySettings}
          onVisibilityChange={onCardDisplaySettingsChange}
          customFields={customFields}
          onManageCustomFields={onManageCustomFields}
        />
      </ResponsiveToolbar.Item>

      {/* ═══════════════════════════════════════════════════════════════
          VIEW-SPECIFIC ELEMENTS (Flow only)
          ═══════════════════════════════════════════════════════════════ */}

      {/* Flow-specific: Go to Start - Priority 7 */}
      <ResponsiveToolbar.Item priority={7}>
        <ToolbarBtn
          icon={<HomeIcon />}
          label="Go To Start"
          onClick={() => (onGoToStart ? onGoToStart() : flowchartRef.current?.goToStart())}
          title="Go to start"
        />
      </ResponsiveToolbar.Item>

      {/* Flow-specific: HUD Visibility - Priority 10 */}
      <ResponsiveToolbar.Item priority={10}>
        <VisibilityDropdown
          visibility={flowViewVisibility}
          onVisibilityChange={onFlowViewVisibilityChange}
          isMultiCampaignMode={isMultiCampaignMode}
        />
      </ResponsiveToolbar.Item>

      {onResetCampaignProgress && (
        <ResponsiveToolbar.Item priority={11} label="Reset campaign">
          <ResetCampaignToolbarItem onResetCampaignProgress={onResetCampaignProgress} />
        </ResponsiveToolbar.Item>
      )}
      {onManageVariables && (
        <ResponsiveToolbar.Item priority={12} label="Variables">
          <VariablesToolbarItem onManageVariables={onManageVariables} />
        </ResponsiveToolbar.Item>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SEARCH (ALL views - always at end)
          ═══════════════════════════════════════════════════════════════ */}
      
      {/* Mission Search - Priority 4 (important, stays visible) */}
      <ResponsiveToolbar.Item priority={4}>
        <ToolbarBtn
          icon={<SearchIcon />}
          label="Search"
          onClick={onOpenMissionSearch}
          title="Search missions (Ctrl+F)"
        />
      </ResponsiveToolbar.Item>

    </ResponsiveToolbar>
  );
};

export default FlowToolbar;
